import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatDate, titleCase } from '../../lib/format'
import type { ComplianceNotice, UserProfile } from '../../types'
import type { Inspection, MiningSite } from './types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { RangeChips, WeeklyLineChart } from '../../components/charts'
import { CheckIcon, DocIcon, IdIcon, ShieldIcon } from '../../components/icons'
import '../miner/dashboard.css'

interface GovSeries {
  weeks: string[]
  inspections: number[]
  notices: number[]
  sites_registered: number[]
}

interface GovDashboard {
  ok: boolean
  series?: GovSeries
  user: UserProfile
  kpis: {
    inspections_total: number
    open_notices: number
    pending_validations: number
    suspended_licences: number
  }
  recent_inspections: Inspection[]
  recent_notices: ComplianceNotice[]
  pending_sites: MiningSite[]
}

export default function GovDashboard() {
  const { user } = useAuth()
  const [tab, setTab] = useState<'summary' | 'trends'>('summary')
  const [weeks, setWeeks] = useState(8)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'dashboard', weeks],
    queryFn: () => api.get<GovDashboard>(`/gov/dashboard?weeks=${weeks}`),
  })

  const k = data?.kpis
  const scope = user?.district_id || user?.region_id

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Regulatory overview</h1>
          <p className="page-head__sub">
            {scope
              ? <>Activity within your assigned area (<strong>{scope}</strong>).</>
              : 'Activity across all regions.'}
          </p>
        </div>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('summary')}>
          Summary
        </Button>
        <Button variant={tab === 'trends' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('trends')}>
          Trends
        </Button>
      </div>

      {tab === 'trends' && (
        <div className="stack" style={{ gap: 'var(--sp-5)' }}>
          <RangeChips value={weeks} onChange={setWeeks} />
          <Card>
            <CardHeader title="Inspections per week" sub={`Site visits recorded in your area, last ${weeks} weeks`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart weeks={data.series.weeks} series={[{ label: 'Inspections', values: data.series.inspections }]} />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Notices & new sites per week" sub={`Compliance notices issued vs sites registered, last ${weeks} weeks`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart
                  weeks={data.series.weeks}
                  series={[
                    { label: 'Notices', values: data.series.notices },
                    { label: 'New sites', values: data.series.sites_registered },
                  ]}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'summary' && (<>
      <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-6)' }}>
        <Kpi label="Sites awaiting validation" value={k?.pending_validations}
             meta="need a decision" to="/sites" icon={<IdIcon />}
             loading={isLoading} urgent={!!k?.pending_validations} />
        <Kpi label="Open notices" value={k?.open_notices}
             meta="unresolved" to="/notices" icon={<DocIcon />}
             loading={isLoading} urgent={!!k?.open_notices} />
        <Kpi label="Suspended licences" value={k?.suspended_licences}
             meta="not currently valid" to="/licences" icon={<ShieldIcon />}
             loading={isLoading} urgent={!!k?.suspended_licences} />
        <Kpi label="Inspections" value={k?.inspections_total}
             meta="on record" to="/inspections" icon={<CheckIcon />}
             loading={isLoading} />
      </div>

      <div className="grid grid--2">
        <Card>
          <CardHeader title="Sites awaiting validation"
                      action={<Link to="/sites" style={{ fontSize: 'var(--text-sm)' }}>Review all</Link>} />
          <CardBody flush>
            {isLoading ? <Loading /> : !data?.pending_sites.length ? (
              <EmptyState icon={<CheckIcon size={22} />} title="Nothing pending"
                          body="Every registered site in your area has been assessed." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Site</th><th>District</th><th>Mineral</th><th>Status</th></tr></thead>
                  <tbody>
                    {data.pending_sites.map((s, i) => (
                      <tr key={s.id ?? i}>
                        <td>{s.name || s.id || '—'}</td>
                        <td>{s.district_id || '—'}</td>
                        <td>{titleCase(s.mineral_type)}</td>
                        <td><StatusBadge status={s.validation_status || 'pending'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Recent inspections"
                      action={<Link to="/inspections" style={{ fontSize: 'var(--text-sm)' }}>View all</Link>} />
          <CardBody flush>
            {isLoading ? <Loading /> : !data?.recent_inspections.length ? (
              <EmptyState icon={<CheckIcon size={22} />} title="No inspections recorded"
                          body="Inspections you record will be listed here." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Date</th><th>Outcome</th><th>Findings</th></tr></thead>
                  <tbody>
                    {data.recent_inspections.map((ins, i) => (
                      <tr key={ins.id ?? i}>
                        <td>{formatDate(ins.inspection_date ?? ins.date)}</td>
                        <td><StatusBadge status={ins.outcome} /></td>
                        <td style={{ maxWidth: 280 }}>{ins.findings || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
      </>)}
    </>
  )
}

function Loading() {
  return (
    <div style={{ padding: 'var(--sp-6)' }} className="stack">
      <Skeleton h={18} /><Skeleton h={18} w="72%" /><Skeleton h={18} w="58%" />
    </div>
  )
}

function Kpi({
  label, value, meta, icon, to, loading, urgent,
}: {
  label: string; value?: number; meta?: string; icon?: React.ReactNode
  to: string; loading: boolean; urgent?: boolean
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card hover>
        <div className="stat">
          <div className="stat__top">
            <span className="stat__label">{label}</span>
            {icon && (
              <span className="stat__icon"
                    style={urgent ? { background: 'var(--warning-bg)', color: 'var(--warning)' } : undefined}>
                {icon}
              </span>
            )}
          </div>
          {loading ? <Skeleton h={34} w="50%" /> : <div className="stat__value">{value ?? 0}</div>}
          {meta && <div className="stat__meta">{meta}</div>}
        </div>
      </Card>
    </Link>
  )
}
